import {
  Box,
  Button,
  CircularProgress,
  Fade,
  Modal,
  TextField,
  Typography
} from "@mui/material";
import { CircleX } from "lucide-react";
import { useRef, useState } from "react";
import { useToastStore, useUserStore } from "../../../stores";
import { colorPrimary } from "../../../theme";
import { formatCurrencyBRL, maskCurrencyBRL } from "../../../utils/mask/MaskUtils";
import { ProposalCombo } from "../dto/ShopkeeperDTO";
import { submitOfferAnuncio } from "../repository/ShopkeeperRepository";



interface ModalPropostaProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: (message: string) => Promise<void>;
  val_minimoProposta: number;
  data_inicio: string;
  tipo_anuncio: string;
  combo_id?: number;
}

export function PropostaModal({
  open,
  onClose,
  onSuccess,
  val_minimoProposta,
  tipo_anuncio,
  combo_id,
}: ModalPropostaProps) {
  const { user,} = useUserStore();
  const { setToast } = useToastStore();
  const [isLoading, setLoading] = useState(false);
  const val_proposta = useRef("");



const handleSubmitProposal = async () => {
  setLoading(true);

  try {
    // Converte valor mascarado para número
    const valorProposta =val_proposta.current.replaceAll("R\$","").replaceAll(".","").replaceAll(",",".").trim() === "" ? 0 : parseFloat(val_proposta.current.replaceAll("R\$","").replaceAll(".","").replaceAll(",","."));;
    const valPropostaFormatted = Number(valorProposta.toFixed(2));
    if (valPropostaFormatted < val_minimoProposta) {
      setToast({ message:"O valor da proposta é menor que o mínimo permitido.", variant: 'error' });
      return;
    }
    const payload : ProposalCombo ={
      num_id_anuncio: combo_id ?? 0,
      val_proposta: valPropostaFormatted,
      tag_compra_ou_proposta: "0",
      tag_origem: "0",
    };
  const result = await submitOfferAnuncio(payload);
    if (result.success) {
      setToast({ message:"Proposta enviada com sucesso!", variant: 'success' });
      if (onSuccess) await onSuccess("Proposta enviada com sucesso!");
      onClose();
    } else {
      setToast({ message:result.message || "Erro ao enviar proposta.", variant: 'error' });
    }
  } catch (error) {
    setToast({ message: "Erro ao enviar proposta.", variant: 'error' });
  } finally {
    setLoading(false); // GARANTIDO
  }
};



return (
  <>
    {/* TOAST */}
    <Modal
      open={open}
      onClose={onClose}
      closeAfterTransition
      aria-labelledby="confirmation-modal-title"
      aria-describedby="confirmation-modal-description"
    >
      <Fade in={open}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "100%",
            maxWidth: 420,
            bgcolor: "white",
            borderRadius: 2,
            boxShadow: 24,
            outline: "none",
          }}
        >
        <Box sx={{height: 8,bgcolor: colorPrimary,borderTopLeftRadius: 8,borderTopRightRadius: 8, }}/>
          {/* HEADER */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 3,
              py: 2,
            }}
          >
            <Typography
              id="confirmation-modal-title"
              fontSize="1.4rem"
              fontWeight={700}
            >
              Confirmação!
            </Typography>

            <Box
              onClick={onClose}
              sx={{
                width: 32,
                height: 32,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: 1,
                cursor: "pointer",
                color: colorPrimary,
                transition: "all 0.25s ease",
                "&:hover": {
                  backgroundColor: "rgba(255, 80, 80, 0.15)",
                  boxShadow: "0 0 12px rgba(255, 80, 80, 0.45)",
                  transform: "rotate(90deg) scale(1.05)",
                },
              }}
            >
              <CircleX size={28} />
            </Box>
          </Box>

          {/* CONTENT */}
          <Box
            id="confirmation-modal-description"
            sx={{px: 4, mb:1}} >
            <Typography fontWeight={400} fontSize="1rem">
            Valor mínimo da proposta{" "}
            <Box
                component="span"
                fontWeight={600}
                color={colorPrimary}
            >
                {formatCurrencyBRL(val_minimoProposta ?? 0)}
            </Box>
            </Typography>
          </Box>
          <Box
            id="confirmation-modal-description"
            sx={{ px: 4, }}
            >
          <TextField
            fullWidth
            size="small"
            placeholder="Digite o valor da proposta"
            inputMode="numeric"
            onChange={(e) => {
              const maskedValue = maskCurrencyBRL(e.target.value);
              e.target.value = maskedValue; 
              val_proposta.current = maskedValue; 
            }}
            sx={{
              border: `1.5px solid ${colorPrimary}`,
              borderRadius: 2,
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  border: "none",
                },
              },
            }}
          />
          </Box>

          {/* ACTIONS */}
          <Box
            sx={{
              px: 4,
              py: 3,
              display: "flex",
              justifyContent: "flex-end",
              gap: 2,
            }}
          >
            <Button
              variant="text"
              onClick={onClose}
              sx={{
                width: 150,
                color: colorPrimary,
                borderRadius: 2.5,
                  "&:hover": {
                  transform: "translateY(-1px)",
                },
              }}
            >
              Cancelar
            </Button>

            <Button
              disabled={isLoading}
              onClick={async () => handleSubmitProposal()}
              sx={{
                width: 200,
                background: colorPrimary,
                boxShadow: "0 0 20px rgba(245,159,10,0.35)",
                transition: "0.25s ease",
                borderRadius: 2.5,
                "&:hover": {
                  background: colorPrimary,
                  boxShadow: "0 0 25px rgba(245,159,10,0.55)",
                  transform: "translateY(-1px)",
                },
                color: "#fff",
                fontWeight: 600,
              }}
            >
              {isLoading ? <CircularProgress size={26} sx={{ color: "#fff" }} /> : "Confirmar"}
            </Button>
          </Box>
        </Box>
      </Fade>
    </Modal>
  </>
);
}
